import type {
  AnalyticsAffordableRentDto,
  AnalyticsAnnualIncomeDto,
  CustomerAnalyticsInsightsRenterExpectationDto,
  RenterActivityFilterRegionStatsDto,
  RenterActivityKeywordRegionStatsDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { AnalyticsAffordableRentBracket, AnalyticsAnnualIncomeBucket } from '@zg-rentals/ts-customer-analytics-client';

export type PercentageDatum = {
  label: string;
  value: number;
};

export const formatEnumLabel = (value: string): string => {
  let label = value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .trim();

  label = label
    .replace(/^Rental\s+/i, '')
    .replace(/^Rent\s+/i, '')
    .replace(/\s+Ind$/i, '')
    .replace(/\s+Allowed$/i, '')
    .replace(/\bBathroom\s+(\d+)\s+Plus\b/gi, '$1 bath')
    .replace(/\bBedroom\s+(\d+)\s+Plus\b/gi, '$1 bed')
    .replace(/\bBathroom\s+Any\b/gi, 'Any bath')
    .replace(/\bBedroom\s+Any\b/gi, 'Studio')
    .replace(/\bCats\b/gi, 'Cats')
    .replace(/\bLarge Pets\b/gi, 'Dogs')
    .replace(/\bSmall Pets\b/gi, 'Other pets')
    .replace(/\bNo Pets\b/gi, 'No pets');

  return label;
};

export const aggregateFilterStats = (
  regionStats: Array<RenterActivityFilterRegionStatsDto>,
): Array<PercentageDatum> => {
  if (regionStats.length === 0) return [];

  const aggregatedByType = new Map<string, { total: number; count: number }>();

  regionStats.forEach((region) => {
    region.stats.forEach((stat) => {
      const key = stat.filterType.toString();
      const current = aggregatedByType.get(key) ?? { total: 0, count: 0 };
      current.total += parseFloat(stat.percentage.toString());
      current.count += 1;
      aggregatedByType.set(key, current);
    });
  });

  return Array.from(aggregatedByType.entries()).map(([filterType, { total, count }]) => ({
    label: formatEnumLabel(filterType),
    value: count > 0 ? Math.round((total / count) * 100 * 10) / 10 : 0,
  }));
};

export const aggregateKeywordStats = (
  regionStats: Array<RenterActivityKeywordRegionStatsDto>,
): Array<PercentageDatum> => {
  if (regionStats.length === 0) return [];

  const aggregatedByKeyword = new Map<string, { total: number; count: number }>();

  regionStats.forEach((region) => {
    region.stats.forEach((stat) => {
      const key = stat.keyword;
      const current = aggregatedByKeyword.get(key) ?? { total: 0, count: 0 };
      current.total += parseFloat(stat.percentage.toString());
      current.count += 1;
      aggregatedByKeyword.set(key, current);
    });
  });

  const allKeywords = Array.from(aggregatedByKeyword.entries()).map(([keyword, { total, count }]) => ({
    label: keyword,
    value: count > 0 ? Math.round((total / count) * 100 * 10) / 10 : 0,
  }));

  return allKeywords.sort((statA, statB) => statB.value - statA.value).slice(0, 5);
};

const INCOME_BRACKET_LABELS: Record<AnalyticsAnnualIncomeBucket, string> = {
  [AnalyticsAnnualIncomeBucket.BRACKET_0_40K]: '$0-$40,000',
  [AnalyticsAnnualIncomeBucket.BRACKET_40K_60K]: '$40,001-$60,000',
  [AnalyticsAnnualIncomeBucket.BRACKET_60K_80K]: '$60,001-$80,000',
  [AnalyticsAnnualIncomeBucket.BRACKET_80K_120K]: '$80,001-$120,000',
  [AnalyticsAnnualIncomeBucket.BRACKET_120K_160K]: '$120,001-$160,000',
  [AnalyticsAnnualIncomeBucket.BRACKET_160K_PLUS]: '$160,001+',
};

const INCOME_BRACKET_ORDER: Array<AnalyticsAnnualIncomeBucket> = [
  AnalyticsAnnualIncomeBucket.BRACKET_0_40K,
  AnalyticsAnnualIncomeBucket.BRACKET_40K_60K,
  AnalyticsAnnualIncomeBucket.BRACKET_60K_80K,
  AnalyticsAnnualIncomeBucket.BRACKET_80K_120K,
  AnalyticsAnnualIncomeBucket.BRACKET_120K_160K,
  AnalyticsAnnualIncomeBucket.BRACKET_160K_PLUS,
];

const RENT_RANGE_LABELS = [
  'Under $1,000',
  '$1,001-$1,500',
  '$1,501-$2,000',
  '$2,001-$3,000',
  '$3,001-$4,000',
  '$4,001-$5,000',
];

export const mapAnnualIncomeDistribution = (data: Array<AnalyticsAnnualIncomeDto>): Array<PercentageDatum> => {
  if (!data || data.length === 0) return [];

  const hasValidData = data.some((item) => item.percentage > 0);
  if (!hasValidData) return [];

  return INCOME_BRACKET_ORDER.map((bucket) => {
    const item = data.find((d) => d.bracket === bucket);
    return {
      label: INCOME_BRACKET_LABELS[bucket],
      value: Math.round((item?.percentage ?? 0) * 10) / 10,
    };
  }).filter((d) => d.value > 0);
};

export const mapRenterExpectationsToRent = (
  data: CustomerAnalyticsInsightsRenterExpectationDto,
): Array<PercentageDatum> => {
  if (!data.renterPriceRange || data.renterPriceRange.length === 0) {
    return [];
  }

  const hasValidData = data.renterPriceRange.some((value) => value > 0);
  if (!hasValidData) {
    return [];
  }

  return data.renterPriceRange.map((value, index) => ({
    label: RENT_RANGE_LABELS[index] || `Range ${index + 1}`,
    value: Math.round(value * 10) / 10,
  }));
};

const BEDROOM_SORT_ORDER: Record<string, number> = {
  Studio: 0,
  '1 bed': 1,
  '2 bed': 2,
  '3 bed': 3,
  '4 bed': 4,
};

export const sortBedroomData = (data: Array<PercentageDatum>): Array<PercentageDatum> => {
  return [...data].sort((a, b) => {
    const orderA = BEDROOM_SORT_ORDER[a.label] ?? 999;
    const orderB = BEDROOM_SORT_ORDER[b.label] ?? 999;
    return orderA - orderB;
  });
};

export type QuartileData = {
  label: string;
  value: string;
};

const formatDollar = (val: number): string => `$${val.toLocaleString()}`;

const BRACKET_ORDER: Array<AnalyticsAffordableRentBracket> = [
  AnalyticsAffordableRentBracket.UNDER_1K,
  AnalyticsAffordableRentBracket.BRACKET_1K_1500,
  AnalyticsAffordableRentBracket.BRACKET_1500_2K,
  AnalyticsAffordableRentBracket.BRACKET_2K_3K,
  AnalyticsAffordableRentBracket.BRACKET_3K_4K,
  AnalyticsAffordableRentBracket.BRACKET_4K_PLUS,
];

const BRACKET_BOUNDARIES: Array<[number, number]> = [
  [0, 1000],
  [1000, 1500],
  [1500, 2000],
  [2000, 3000],
  [3000, 4000],
  [4000, 5000],
];

export const mapAffordableRentDistributionToQuartiles = (
  data: Array<AnalyticsAffordableRentDto>,
): Array<QuartileData> | null => {
  if (!data || data.length === 0) return null;

  const percentageByBracket = new Map(data.map((d) => [d.bracket, d.percentage]));
  const orderedPercentages = BRACKET_ORDER.map((bracket) => percentageByBracket.get(bracket) ?? 0);

  const total = orderedPercentages.reduce((sum, val) => sum + val, 0);
  if (total <= 0) return null;

  const interpolate = (percentile: number): number => {
    const target = (percentile / 100) * total;
    let cumulative = 0;

    for (let i = 0; i < orderedPercentages.length; i++) {
      const prev = cumulative;
      cumulative += orderedPercentages[i];

      if (target <= cumulative) {
        const [low, high] = BRACKET_BOUNDARIES[i];
        const width = orderedPercentages[i];
        const fraction = width > 0 ? (target - prev) / width : 0;
        return Math.round((low + fraction * (high - low)) / 25) * 25;
      }
    }

    return BRACKET_BOUNDARIES[BRACKET_BOUNDARIES.length - 1][1];
  };

  const q25 = interpolate(25);
  const q50 = interpolate(50);
  const q75 = interpolate(75);

  return [
    { label: 'Bottom 25%', value: `Under ${formatDollar(q25)}` },
    { label: '25-50%', value: `${formatDollar(q25)}-\n${formatDollar(q50)}` },
    { label: '50-75%', value: `${formatDollar(q50)}-\n${formatDollar(q75)}` },
    { label: 'Top 25%', value: `${formatDollar(q75)} and up` },
  ];
};

type MovingGraphCopy = {
  searchingForTitle: string;
  searchingForTooltip: string;
  searchingFromTitle: string;
  searchingFromTooltip: string;
};

export const getMovingGraphCopy = (
  regionLevel: string,
  areaName: string,
  isNationwide: boolean,
  cityName?: string,
): MovingGraphCopy => {
  if (isNationwide) {
    return {
      searchingForTitle: 'Nationwide cities renters are searching for',
      searchingForTooltip: `Based on rental searches where the searcher is looking outside their current city. Shows the top 10 destination cities by share of total cross-city rental searches nationwide.`,
      searchingFromTitle: 'Nationwide cities renters are searching from',
      searchingFromTooltip: `Based on rental searches where the searcher is looking outside their current city. Shows the top 10 origin cities by share of total cross-city rental searches nationwide.`,
    };
  }

  if (regionLevel === 'state') {
    return {
      searchingForTitle: `${areaName} cities renters are searching for`,
      searchingForTooltip: `Based on rental searches where the searcher is looking outside their current city. Shows the top 10 ${areaName} cities attracting the most inbound rental searches from other cities.`,
      searchingFromTitle: `${areaName} cities renters are searching from`,
      searchingFromTooltip: `Based on rental searches where the searcher is looking outside their current city. Shows the top 10 ${areaName} cities with the most outbound rental searches to other cities.`,
    };
  }

  if (regionLevel === 'metroArea') {
    return {
      searchingForTitle: `Where ${areaName} renters are looking`,
      searchingForTooltip: `Based on rental searches where renters currently in the ${areaName} metro are searching for rentals in a different city. Shows the top 10 destination cities by search share.`,
      searchingFromTitle: `Where interest in ${areaName} is coming from`,
      searchingFromTooltip: `Based on rental searches where renters outside the ${areaName} metro are searching for rentals in this area. Shows the top 10 origin cities of those searchers.`,
    };
  }

  const effectiveName = regionLevel === 'zip' && cityName ? cityName : areaName;
  const zipNote = regionLevel === 'zip' ? 'Showing city data, as ZIP code-level data not available. ' : '';

  return {
    searchingForTitle: `Where ${effectiveName} renters are looking`,
    searchingForTooltip: `${zipNote}Based on rental searches where renters currently in ${effectiveName} are searching for rentals in a different city. Shows the top 10 destination cities by search share.`,
    searchingFromTitle: `Where interest in ${effectiveName} is coming from`,
    searchingFromTooltip: `${zipNote}Based on rental searches where renters outside ${effectiveName} are searching for rentals in ${effectiveName}. Shows the top 10 origin cities of those searchers.`,
  };
};

export const calculatePetOwnershipPercentage = (data: CustomerAnalyticsInsightsRenterExpectationDto): number | null => {
  const petSum = (data.percentPetsCat ?? 0) + (data.percentPetsDog ?? 0) + (data.percentPetsOther ?? 0);
  if (petSum <= 0) {
    return null;
  }
  return Math.round(petSum * 1000) / 10;
};
